import { useCallback, useEffect, useRef, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'

type SetValue<T> = Dispatch<SetStateAction<T>>

export function useLocalStorage<T>(key: string, initialValue: T | (() => T)): [T, SetValue<T>] {
  const initialValueRef = useRef<T>(
    typeof initialValue === 'function'
      ? (initialValue as () => T)()
      : initialValue
  )

  const isStorageAvailable =
    typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'

  const readValue = useCallback((): T => {
    if (!isStorageAvailable) {
      return initialValueRef.current
    }

    try {
      const item = window.localStorage.getItem(key)
      return item ? (JSON.parse(item) as T) : initialValueRef.current
    } catch (error) {
      console.warn(`Failed to read localStorage key "${key}":`, error)
      return initialValueRef.current
    }
  }, [isStorageAvailable, key])

  const [storedValue, setStoredValue] = useState<T>(() => readValue())

  useEffect(() => {
    setStoredValue(readValue())
  }, [readValue])

  useEffect(() => {
    if (!isStorageAvailable) return

    const handleStorage = (event: StorageEvent) => {
      if (event.storageArea !== window.localStorage) return
      if (event.key !== key) return

      if (event.newValue === null) {
        setStoredValue(initialValueRef.current)
        return
      }

      try {
        setStoredValue(JSON.parse(event.newValue) as T)
      } catch (error) {
        console.warn(`Failed to parse localStorage key "${key}":`, error)
        setStoredValue(initialValueRef.current)
      }
    }

    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [isStorageAvailable, key])

  const setValue: SetValue<T> = useCallback(
    (value) => {
      setStoredValue((prev) => {
        const valueToStore =
          typeof value === 'function'
            ? (value as (prevState: T) => T)(prev)
            : value

        if (isStorageAvailable) {
          try {
            if (valueToStore === undefined) {
              window.localStorage.removeItem(key)
            } else {
              window.localStorage.setItem(key, JSON.stringify(valueToStore))
            }
          } catch (error) {
            console.warn(`Failed to set localStorage key "${key}":`, error)
          }
        }

        return valueToStore
      })
    },
    [isStorageAvailable, key]
  )

  return [storedValue, setValue]
}
